import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/RichTextEditor";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Home, 
  Phone, 
  MapPin, 
  Globe, 
  Utensils, 
  Coffee, 
  Send, 
  Loader2,
  CheckCircle2,
  Clock,
  IndianRupee,
  Image,
  User,
  Mail
} from "lucide-react";
import api from "@/lib/api";

const FACILITIES_OPTIONS = [
  "WiFi",
  "Parking",
  "Air Conditioning",
  "Swimming Pool",
  "Garden",
  "Kitchen",
  "TV",
  "Hot Water",
  "Room Service",
  "Laundry",
  "Pet Friendly",
  "Beach Access",
  "Mountain View",
  "Bonfire",
  "BBQ",
  "Trekking",
];

const ListYourHomestayPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [form, setForm] = useState({
    // Owner Info
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    
    // Property Info
    name: "",
    address: "",
    mobile_number: "",
    website: "",
    google_business_url: "",
    description: "",
    
    // Amenities
    facilities: [],
    meal_included: false,
    breakfast_included: false,
    
    // Timing & Price
    check_in_time: "14:00",
    check_out_time: "11:00",
    price_per_night: "",
    
    // Additional
    message: "",
  });

  const handleFacilityToggle = (facility) => {
    setForm(prev => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter(f => f !== facility)
        : [...prev.facilities, facility]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.owner_name || !form.owner_email || !form.owner_phone) {
      toast.error("Please fill in your contact details");
      return;
    }
    if (!form.name || !form.address || !form.mobile_number) {
      toast.error("Please fill in property name, address, and contact number");
      return;
    }

    setLoading(true);
    try {
      // Submit as an inquiry with special type
      await api.post("/inquiries", {
        homestay_id: "listing_request",
        name: form.owner_name,
        email: form.owner_email,
        phone: form.owner_phone,
        message: `
**NEW HOMESTAY LISTING REQUEST**

**Owner Details:**
- Name: ${form.owner_name}
- Email: ${form.owner_email}
- Phone: ${form.owner_phone}

**Property Details:**
- Property Name: ${form.name}
- Address: ${form.address}
- Contact Number: ${form.mobile_number}
- Website: ${form.website || "Not provided"}
- Google My Business: ${form.google_business_url || "Not provided"}
- Description: ${form.description || "Not provided"}

**Amenities:**
- Facilities: ${form.facilities.join(", ") || "None selected"}
- Meals Included: ${form.meal_included ? "Yes" : "No"}
- Breakfast Included: ${form.breakfast_included ? "Yes" : "No"}

**Timing & Pricing:**
- Check-in: ${form.check_in_time}
- Check-out: ${form.check_out_time}
- Price per Night: ₹${form.price_per_night || "Not specified"}

**Additional Message:**
${form.message || "None"}
        `.trim(),
        check_in: null,
        check_out: null,
        guests: 0,
      });

      setSubmitted(true);
      toast.success("Your listing request has been submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Helmet>
          <title>List Your Homestay</title>
        </Helmet>
        <Navbar />
        
        <div className="pt-24 pb-16 px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 
              className="text-3xl md:text-4xl font-medium text-foreground mb-4"
             
            >
              Thank You!
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Your homestay listing request has been submitted successfully. Our team will review your property details and get back to you within 2-3 business days.
            </p>
            <div className="bg-white rounded-xl p-6 shadow-soft text-left">
              <h3 className="font-semibold text-foreground mb-3">What happens next?</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                  <span>Our team reviews your submission</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                  <span>We may contact you for additional details or photos</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                  <span>Once approved, your homestay goes live on Kokan Homestays</span>
                </li>
              </ul>
            </div>
            <Button 
              onClick={() => window.location.href = "/"} 
              className="mt-8 rounded-full"
            >
              Back to Home
            </Button>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="list-homestay-page">
      <Helmet>
        <title>List Your Homestay</title>
        <meta name="description" content="List your property on Kokan Homestays. Reach thousands of travelers looking for authentic homestay experiences in the Konkan region." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${window.location.origin}/list-your-homestay`} />
      </Helmet>
      
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 md:px-12 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-4xl md:text-5xl font-medium text-foreground mb-4"
           
          >
            List Your <span className="text-primary">Homestay</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join Kokan Homestays and reach thousands of travelers looking for authentic homestay experiences in the beautiful Konkan region.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-5 shadow-soft text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Globe className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">Wide Reach</h3>
            <p className="text-sm text-muted-foreground">Get discovered by travelers across India</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-soft text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <IndianRupee className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">Free to List</h3>
            <p className="text-sm text-muted-foreground">No charges for listing your property</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-soft text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">Easy Process</h3>
            <p className="text-sm text-muted-foreground">Simple submission, quick approval</p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="list-homestay-form">
            
            {/* Owner Information */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Your Contact Information</h2>
                  <p className="text-sm text-muted-foreground">We'll use this to contact you about your listing</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Your Name *</label>
                  <Input
                    value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                    placeholder="Full name"
                    data-testid="owner-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Email *</label>
                  <Input
                    type="email"
                    value={form.owner_email}
                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="owner-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Phone *</label>
                  <Input
                    value={form.owner_phone}
                    onChange={(e) => setForm({ ...form, owner_phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    data-testid="owner-phone"
                  />
                </div>
              </div>
            </div>

            {/* Property Information */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Property Details</h2>
                  <p className="text-sm text-muted-foreground">Tell us about your homestay</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Homestay Name *</label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g., Sunset Beach Villa"
                      data-testid="property-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Contact Number *</label>
                    <Input
                      value={form.mobile_number}
                      onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                      placeholder="Property contact number"
                      data-testid="property-phone"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Full Address *</label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Village/Town, Taluka, District, State"
                    data-testid="property-address"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Website (Optional)</label>
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://yourhomestay.com"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Google My Business URL (Optional)</label>
                  <Input
                    value={form.google_business_url}
                    onChange={(e) => setForm({ ...form, google_business_url: e.target.value })}
                    placeholder="https://g.page/your-business or Google Maps link"
                    data-testid="google-business-url"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Share your Google Business profile or Google Maps listing link
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Description</label>
                  <RichTextEditor
                    value={form.description}
                    onChange={(value) => setForm({ ...form, description: value })}
                    placeholder="Describe your property - what makes it special, nearby attractions, unique experiences you offer..."
                    minHeight="120px"
                  />
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Facilities & Amenities</h2>
                  <p className="text-sm text-muted-foreground">Select what you offer to guests</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {FACILITIES_OPTIONS.map((facility) => (
                  <label
                    key={facility}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      form.facilities.includes(facility)
                        ? "bg-primary/5 border-primary"
                        : "hover:bg-muted"
                    }`}
                  >
                    <Checkbox
                      checked={form.facilities.includes(facility)}
                      onCheckedChange={() => handleFacilityToggle(facility)}
                    />
                    <span className="text-sm">{facility}</span>
                  </label>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-6 pt-4 border-t">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.meal_included}
                    onCheckedChange={(c) => setForm({ ...form, meal_included: c })}
                  />
                  <div className="flex items-center gap-2">
                    <Utensils className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Meals Included</span>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.breakfast_included}
                    onCheckedChange={(c) => setForm({ ...form, breakfast_included: c })}
                  />
                  <div className="flex items-center gap-2">
                    <Coffee className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Breakfast Included</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Timing & Pricing */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Timing & Pricing</h2>
                  <p className="text-sm text-muted-foreground">Set your check-in/out times and pricing</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Check-in Time</label>
                  <Input
                    type="time"
                    value={form.check_in_time}
                    onChange={(e) => setForm({ ...form, check_in_time: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Check-out Time</label>
                  <Input
                    type="time"
                    value={form.check_out_time}
                    onChange={(e) => setForm({ ...form, check_out_time: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Price per Night (₹)</label>
                  <Input
                    type="number"
                    value={form.price_per_night}
                    onChange={(e) => setForm({ ...form, price_per_night: e.target.value })}
                    placeholder="e.g., 2500"
                  />
                </div>
              </div>
            </div>

            {/* Additional Message */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Additional Information</h2>
                  <p className="text-sm text-muted-foreground">Any other details you'd like to share</p>
                </div>
              </div>
              
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us anything else about your property, special offers, or questions you have..."
                rows={3}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button 
                type="submit" 
                size="lg" 
                className="rounded-full px-12"
                disabled={loading}
                data-testid="submit-listing"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Listing Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ListYourHomestayPage;
